import React, { useState, useRef, useMemo, useEffect, forwardRef, useImperativeHandle } from "react";
import "./TransfersForm.css";
import { Accordion, Col, Row, Form, Spinner, ProgressBar, Modal } from "react-bootstrap";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { SingleCalendar } from "../../../../components";
import { ErrorPopUp } from "../../../../components";
import SignatureCanvas from "react-signature-canvas";
import { ReactComponent as UploadIcon } from "../../../../assets/images/icons/solar_upload-line-duotone.svg";
import Cookies from "js-cookie";
import { useTranslation } from "../../../../contexts/TranslationContext";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import debounce from "lodash.debounce";
import axios from "axios";
import PayloadBuilder from "./PayloadBuilder";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { axiosGet } from "../../../../utils/apis/api";

const TransfersForm = forwardRef(
  (
    {
      transportationItems: initialItems = [],
      onReturn,
      quotationId,
      onSubmit,
      isEditMode = false,
      editingComponent,
      url,
      selectedDates,
      selectedDestination,
      onNext = () => {},
      onUpdateQuot = () => {},
      setItems
    },
    ref
  ) => {
    /* -------------------------------------------------------------------- state */

    const [previewImage, setPreviewImage] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const Lang = Cookies.get("preferred_language") || "en";
    const { t } = useTranslation();

    const [transportationItems, setTransportationItems] = useState(initialItems);
    const [activeTab, setActiveTab] = useState(isEditMode === false ? transportationItems[0]?.package_component_id : "");
    const [completed, setCompleted] = useState({});
    const lastTabKey = transportationItems.at(-1)?.key;

    /* -------------------------------- Accordion and Validation State -------------------------------- */
    const [activeAccordionKeys, setActiveAccordionKeys] = useState(["0"]);
    const [errors, setErrors] = useState({});

    // Field to accordion mapping for scroll functionality
    const fieldToAccordionMap = {
      // Personal Information (0)
      full_name_as_per_passport: "0",
      nationality: "0",
      contact_number: "0",
      date_of_birth: "0",

      // Transfer Details (1)
      pickup_location: "1",
      dropoff_location: "1",
      pickup_datetime: "1",
      transfer_type: "1",
      preferred_vehicle_type: "1",
      number_of_passengers: "1",

      // Payment & Billing (2)
      payment_method: "2",
      billing_address: "2",
      invoice_required: "2",

      // Travel & Contact (3)
      flight_details: "3",
      hotel_details: "3",
      contact_at_destination: "3",

      // Supporting Documents (4) - file fields handled separately

      // Declaration & Signature (5)
      declaration_agreed: "5",
      digital_or_physical_signature: "5",
    };

    const isClickable = (package_component_id) => {
      if (package_component_id === activeTab) return true;
      const idx = transportationItems.findIndex((item) => item.package_component_id === package_component_id);
      const activeIdx = transportationItems.findIndex((item) => item.package_component_id === activeTab);
      if (idx <= activeIdx) return true;
      return transportationItems.slice(0, idx).every((item) => completed[item.package_component_id]);
    };

    /* C. Errors + upload progress */
    const [globalErr, setGlobalErr] = useState("");
    const [uploads, setUploads] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorPopup, setErrorPopUp] = useState(null);

    const [natQuery, setNatQuery] = useState("");
    const [natOptions, setNatOptions] = useState([]);
    const [natLoading, setNatLoading] = useState(false);
    // const fetchNationalities = useMemo(
    //   () =>
    //     debounce(async (q = "") => {
    //       try {
    //         setNatLoading(true);
    //         const params = q ? { search: q, limit: 20 } : { limit: 20 };
    //         const { data } = await axios.get(url + "/api/method/alltimes.www.api.countries", { params });
    //         setNatOptions(data.result.countries || []);
    //       } catch (e) {
    //         console.error(e);
    //       } finally {
    //         setNatLoading(false);
    //       }
    //     }, 300),
    //   []
    // );

    const fetchNationalities = useMemo(
      () =>
        debounce(async (q = "") => {
          try {
            setNatLoading(true);
            const params = q ? { search: q, limit: 20 } : { limit: 20 };
            const data = await axiosGet(
              "https://viaggioerp.inter-netsys.com/api/method/alltimes.www.api.countries",
              params
            );
            setNatOptions(data?.result?.countries || []);
          } catch (e) {
            console.error(e);
          } finally {
            setNatLoading(false);
          }
        }, 300),
      []
    );

    useEffect(() => {
      fetchNationalities(natQuery);
    }, [natQuery, fetchNationalities]);
    useEffect(() => () => fetchNationalities.cancel(), [fetchNationalities]);

    /* -------------------------------------------------------------------- uploads */
    const inputRefs = useRef({});

    const handleFileChange = (pkgId, fileKey, file) => {
      if (!file) return;

      handleFormChange(pkgId, (prev) => ({
        files: {
          ...prev.files,
          [fileKey]: file,
        },
      }));

      const refKey = `${pkgId}-${fileKey}`;
      if (inputRefs.current[refKey]) {
        inputRefs.current[refKey].value = null;
      }
    };

    const handleFileRemove = (pkgId, fileKey) => {
      handleFormChange(pkgId, (prev) => {
        const newFiles = { ...(prev.files || {}) };
        delete newFiles[fileKey];
        return { files: newFiles };
      });

      const refKey = `${pkgId}-${fileKey}`;
      if (inputRefs.current[refKey]) {
        inputRefs.current[refKey].value = null;
      }
    };

    const truncateFilename = (name, maxLength = 30) => {
      if (!name || name.length <= maxLength) return name;
      const extIndex = name.lastIndexOf(".");
      const ext = extIndex !== -1 ? name.slice(extIndex) : "";
      const base = name.slice(0, maxLength - ext.length - 3);
      return `${base}...${ext}`;
    };

    /* -------------------------------------------------------------------- options */
    const vehicleOptions = [
      { name: "Economy", label: t("Economy") },
      { name: "Business", label: t("Business") },
      { name: "Luxury", label: t("Luxury") },
      { name: "Shuttle", label: t("Shuttle") },
    ];

    const paymentOptions = [
      { name: "Credit Card", label: t("CreditCard") },
      { name: "Apple Pay", label: t("ApplePay") },
      { name: "Bank Transfer", label: t("BankTransfer") },
      { name: "Tabby", label: t("Tabby") },
    ];

    const renderAutocomplete = ({ options, value, setValue, placeholder = "Select", _key }) => (
      <Autocomplete
        fullWidth
        options={options}
        getOptionLabel={(o) => o.label}
        value={value ? options.find((opt) => opt.name === value) : null}
        onChange={(_, newVal) => setValue(newVal?.name || null)}
        isOptionEqualToValue={(opt, val) => opt.name === val}
        sx={{
          ".MuiAutocomplete-inputRoot": {
            fontSize: "12px",
            flexDirection: Lang === "ar" ? "row-reverse" : "row",
          },
          ".MuiAutocomplete-endAdornment": {
            left: Lang === "ar" ? "9 !important" : "unset !important",
            right: Lang === "ar" ? "unset !important" : "9 !important",
          },
          ".MuiInputBase-input": {
            textAlign: Lang === "ar" ? "right" : "left",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              style: {
                direction: Lang === "ar" ? "rtl" : "ltr",
              },
              sx: {
                backgroundColor: "#FFF",
                borderRadius: "6px",
                padding: "9px 15px !important",
                fontFamily: '"Dmsans400"',
                fontSize: "14px !important",
                fontWeight: 400,
                letterSpacing: "0.25px",
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.name}
            style={{
              fontFamily: Lang === "ar" ? "Almarai300" : "Dmsans400",
              direction: Lang === "ar" ? "rtl" : "ltr",
              textAlign: Lang === "ar" ? "right" : "left",
            }}
          >
            {option.label}
          </li>
        )}
      />
    );

    /* -------------------------------------------------------------------- signature */
    const sigPadRef = useRef();
    const clearSignature = () => sigPadRef.current?.clear();

    /* B. Forms state */
    const [formsData, setFormsData] = useState({});
    const handleFormChange = (key, updatedFields) => {
      setFormsData((prev) => {
        const current = prev[key] || {};

        const update = typeof updatedFields === "function" ? updatedFields(current) : updatedFields;

        const updated = {
          ...current,
          ...update,
        };

        // Clear corresponding field errors
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          if (newErrors[key]) {
            for (const field in update) {
              delete newErrors[key][field];
            }
            if (Object.keys(newErrors[key]).length === 0) {
              delete newErrors[key];
            }
          }
          return newErrors;
        });

        return {
          ...prev,
          [key]: updated,
        };
      });
    };

    /* D. Helpers */
    const uploadFile = (file, onProgress) => {
      const apiKey = Cookies.get("api_key");
      const apiSecret = Cookies.get("api_secret");
      const fd = new FormData();
      fd.append("file", file);
      return axios
        .post(url + "/api/method/upload_file?", fd, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `token ${apiKey}:${apiSecret}`,
          },
          onUploadProgress: (e) => onProgress(Math.round((e.loaded * 100) / e.total)),
        })
        .then((r) => r.data?.message?.file_url || "");
    };

    const normalizeFiles = async (files = {}) => {
      const out = { ...files };
      const entries = Object.entries(files).filter(([, v]) => v instanceof File);
      if (!entries.length) return out;

      setUploads(entries.map(([k, f]) => ({ key: k, name: f.name, progress: 0 })));
      setShowModal(true);

      await Promise.all(
        entries.map(([key, file]) =>
          uploadFile(file, (p) =>
            setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x)))
          ).then((url) => {
            out[key] = url;
          })
        )
      );

      setShowModal(false);
      return out;
    };

    const apiKey = Cookies.get("api_key");
    const apiSecret = Cookies.get("api_secret");

    // Validation function
    const validate = (formData) => {
      const newErrors = {};

      // Personal Information validation
      if (!formData?.full_name_as_per_passport) {
        newErrors.full_name_as_per_passport = t("FullNameIsRequired");
      }

      if (!formData?.contact_number) {
        newErrors.contact_number = t("ContactNumberIsRequired");
      }

      if (!formData?.pickup_location) {
        newErrors.pickup_location = t("PickupLocationIsRequired");
      }

      if (!formData?.dropoff_location) {
        newErrors.dropoff_location = t("DropoffLocationIsRequired");
      }

      if (!formData?.pickup_datetime) {
        newErrors.pickup_datetime = t("PickupDateTimeIsRequired");
      }

      if (!formData?.declaration_agreed) {
        newErrors.declaration_agreed = t("YouMustAgreeToTheTerms");
      }

      if (!formData?.digital_or_physical_signature) {
        newErrors.digital_or_physical_signature = t("SignatureIsRequired");
      }

      // Email validation if provided
      if (formData?.contact_number) {
        const phoneRegex = /^\+?[0-9]{6,15}$/;
        if (!phoneRegex.test(formData.contact_number)) {
          newErrors.contact_number = t("InvalidPhoneNumberFormat");
        }
      }

      return newErrors;
    };

    // Scroll to first error function
    const scrollToFirstError = () => {
      const currentFormErrors = errors[activeTab];
      if (currentFormErrors && Object.keys(currentFormErrors).length > 0) {
        const firstErrorKey = Object.keys(currentFormErrors)[0];
        
        // Expand accordion section containing the error
        const accordionKey = fieldToAccordionMap[firstErrorKey];
        if (accordionKey && !activeAccordionKeys.includes(accordionKey)) {
          setActiveAccordionKeys((prev) => [...prev, accordionKey]);
        }

        // Scroll to field after accordion expands
        setTimeout(() => {
          const element = document.getElementById(`field-${firstErrorKey}-${activeTab}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 300);
      }
    };

    // Expose scroll method via ref
    useImperativeHandle(ref, () => ({
      scrollToFirstError,
    }));

    // Auto-expand accordions when errors appear
    useEffect(() => {
      const currentFormErrors = errors[activeTab];
      if (currentFormErrors && Object.keys(currentFormErrors).length > 0) {
        const errorAccordionKeys = Object.keys(currentFormErrors)
          .map((field) => fieldToAccordionMap[field])
          .filter((key) => key !== undefined);

        setActiveAccordionKeys((prev) => [
          ...prev,
          ...errorAccordionKeys.filter((key) => !prev.includes(key)),
        ]);
      }
    }, [errors, activeTab]);

    /* E. Save (Next / Finish) */
    const handleNext = async () => {
      const curKey = activeTab;
      const formData = formsData[curKey];

      // Validate the form
      const validationErrors = validate(formData);
      if (Object.keys(validationErrors).length > 0) {
        setErrors((prev) => ({ ...prev, [curKey]: validationErrors }));
        
        // Expand accordions and scroll to first error
        setTimeout(() => {
          scrollToFirstError();
        }, 100);
        
        return;
      }

      let { payload, errors: fieldErrors } = PayloadBuilder(formData, quotationId);

      if (fieldErrors) {
        setErrors((prev) => ({ ...prev, [curKey]: fieldErrors }));
        
        let fieldToTabMap = {
          full_name_as_per_passport: t("PersonalInformation"),
          declaration_agreed: t("DeclarationAndSignature"),
          digital_or_physical_signature: t("DeclarationAndSignature"),
          dropoff_location: t("TransferDetails"),
          pickup_location: t("TransferDetails"),
          pickup_datetime: t("TransferDetails"),
        };

        const tabSet = new Set();
        Object.keys(fieldErrors).forEach((key) => {
          const tabName = fieldToTabMap[key];
          if (tabName) {
            tabSet.add(tabName);
          }
        });

        const tabsWithErrors = Array.from(tabSet).join(", ");
        toast.error(`${t("missingMandatoryFields")}${tabsWithErrors}`);

        // Scroll to first error
        setTimeout(() => {
          scrollToFirstError();
        }, 100);
        
        return;
      }

      // Clear previous errors
      setErrors((prev) => ({ ...prev, [curKey]: {} }));

      setSaving(true);
      
      // Upload files if any
      try {
        const _upload_files = await normalizeFiles(formData?.files);

        // Helper to extract URL or null
        const extractUrl = (val) => {
          if (Array.isArray(val)) {
            if (val.length === 0) return null;
            return val[0].url;
          }
          return val || null;
        };

        // Flight ticket
        if (_upload_files?.flight_ticket !== undefined) {
          payload.flight_ticket = extractUrl(_upload_files.flight_ticket);
        }

        // Passport copy
        if (_upload_files?.passport_copy !== undefined) {
          payload.passport_copy = extractUrl(_upload_files.passport_copy);
        }
      } catch {
        toast.error(t("uploadFailedTryAgain"), {
          position: "bottom-right",
        });
        setSaving(false);
        setShowModal(false);
        return;
      }

      if (isEditMode) {
        payload = {
          name: formsData[curKey].docName,
          component_row_id: editingComponent.name,
          ...payload,
        };
      }

      const res = await fetch(
        isEditMode
          ? url + "/api/method/alltimes.alltimes_travel.doctype.transfer_reservation.transfer_reservation.update_transfer_reservation"
          : url + "/api/method/alltimes.alltimes_travel.doctype.transfer_reservation.transfer_reservation.create_transfer_reservation",
        {
          method: isEditMode ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${apiKey}:${apiSecret}`,
          },
          body: JSON.stringify(payload),
        }
      ).then((r) => r.json());

      if (res?.status_code == 200 || res?.status_code === "200") {
        const curIdx = transportationItems.findIndex((item) => item.package_component_id === curKey);
        
        let date_from = null;
        let date_to = null;
        
        date_from = formsData[curKey].pickup_datetime
          ? dayjs(formsData[curKey].pickup_datetime).format("YYYY-MM-DD HH:mm:ss")
          : selectedDates?.startDate
          ? dayjs(selectedDates.startDate).format("YYYY-MM-DD HH:mm:ss")
          : null;
          
        date_to = selectedDates?.endDate
          ? dayjs(selectedDates.endDate).format("YYYY-MM-DD HH:mm:ss")
          : null;

        if (isEditMode) {
          setItems(res?.result?.items ?? []);
          setCompleted((p) => ({ ...p, [curKey]: true }));
          setSaving(false);
          toast.success(t("UpdatedSuccessfully"), {
            position: "bottom-right",
            autoClose: 1000,
            pauseOnHover: false,
            closeOnClick: true,
          });
          onReturn();
        } else {
          transportationItems[curIdx].date_from = date_from;
          transportationItems[curIdx].date_to = date_to;
          transportationItems[curIdx].reservation_id = res.result.name;
          transportationItems[curIdx].reservation_type = "Transfer Reservation";
          
          onNext([transportationItems[curIdx]]).then((val) => {
            if (val === true) {
              setCompleted((p) => ({ ...p, [curKey]: true }));

              if (curIdx < transportationItems.length - 1) {
                setActiveTab(transportationItems[curIdx + 1].package_component_id);          
                setSaving(false);            
                return;
              } else {
                console.log("done Add saaaaaaaaaaaaaaaaaaaaaaaa")
                onReturn();
                setSaving(false);
                return;
              }
            }
          });
        }
      } else {
        if (res?.exc) {
          let msg = t("anErrorOccured");
          if (res._server_messages) {
            try {
              const messages = JSON.parse(res._server_messages);
              msg = messages ? JSON.parse(messages[0]).message : null;
            } catch {
            } finally {
              setSaving(false);
            }
          }
          setErrorPopUp(true);
          setGlobalErr(msg);
        } else if (res?.message) {
          setErrorPopUp(true);
          setGlobalErr(res?.message);
        }
        setSaving(false);
        return;
      }
    };

    // Fetch existing data in edit mode
    useEffect(() => {
      if (!isEditMode || !editingComponent) return;

      const fetchData = async () => {
        try {
          const res = await fetch(
            url +
              `/api/method/alltimes.alltimes_travel.doctype.transfer_reservation.transfer_reservation.get_transfer_reservation_data?name=${editingComponent.reservation_id}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `token ${apiKey}:${apiSecret}`,
              },
            }
          ).then((res) => res.json());
          
          if (res.status_code === 200 || res.status_code === "200") {
            const data = res?.result;

            if (data) {
              const passportUrl = typeof data.passport_copy === "string" ? data.passport_copy : "";
              const flightUrl = typeof data.flight_ticket === "string" ? data.flight_ticket : "";

              const formState = {
                ...data,
                passport_copy: passportUrl,
                flight_ticket: flightUrl,
              };

              const files = {
                passport_copy: passportUrl ? [{ url: passportUrl, old: true }] : [],
                flight_ticket: flightUrl ? [{ url: flightUrl, old: true }] : [],
              };

              setFormsData({
                [editingComponent.package_component_id]: {
                  ...formState,
                  files,
                },
              });

              setActiveTab(editingComponent.package_component_id);
            }
          }
        } catch (error) {
          console.error("Failed to fetch transfer reservation data", error);
        }
      };

      fetchData();
    }, [isEditMode, editingComponent]);

    const signatureFromState = formsData[editingComponent?.package_component_id]?.digital_or_physical_signature;
    const isInitialSignatureLoaded = useRef(false);

    useEffect(() => {
      if (signatureFromState && sigPadRef.current && !isInitialSignatureLoaded.current) {
        sigPadRef.current.fromDataURL(signatureFromState);
        isInitialSignatureLoaded.current = true;
      }
    }, [signatureFromState]);

    const handleSignatureChange = (componentId, signatureDataUrl) => {
      setFormsData((prevFormsData) => ({
        ...prevFormsData,
        [componentId]: {
          ...prevFormsData[componentId],
          digital_or_physical_signature: signatureDataUrl,
        },
      }));
    };

    // Helper function to get error for a specific field
    const getError = (fieldName) => {
      return errors[activeTab]?.[fieldName];
    };

    /* -------------------------------------------------------------------- JSX */
    return (
      <div className="hotel-form-container">
        <button className="hotel-form-backbtn" onClick={() => onReturn()}>
          {Lang === "ar" ? (
            <>
              {t("BackToPlan")}
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 23 23" fill="none">
                <path
                  d="M5.75 11.5H18.2083M5.75 11.5L9.58333 7.66671M5.75 11.5L9.58333 15.3334"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 23 23" fill="none">
                <path
                  d="M5.75 11.5H18.2083M5.75 11.5L9.58333 7.66671M5.75 11.5L9.58333 15.3334"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t("BackToPlan")}
            </>
          )}
        </button>

        {/* tabs */}
        <div className="add-services-tabs-scroll-wrapper">
          <Splide
            options={{
              type: "slide",
              gap: "1rem",
              pagination: false,
              arrows: false,
              drag: "free",
              autoWidth: true,
            }}
          >
            {transportationItems.map((item) => (
              <SplideSlide key={item.package_component_id}>
                <div
                  className={`tab-button ${activeTab === item.package_component_id ? "active" : ""} ${
                    !isClickable(item.package_component_id) ? "disabled" : ""
                  }`}
                  onClick={() => {
                    if (isClickable(item.package_component_id)) setActiveTab(item.package_component_id);
                  }}
                >
                  {item.package_component_id}
                </div>
              </SplideSlide>
            ))}
          </Splide>
        </div>

        {transportationItems
          .filter((item) => item.package_component_id === activeTab)
          .map((item) => (
            <Accordion
              alwaysOpen
              activeKey={activeAccordionKeys}
              onSelect={(keys) => setActiveAccordionKeys(keys)}
              className="hotel-form-accordion add-service-form-accordion"
              key={item.package_component_id}
            >
              {/* 1. Personal Information -------------------------------------------------- */}
              <Accordion.Item eventKey="0" className="hotel-form-accordion-item">
                <Accordion.Header className="hotel-form-accordion-header">
                  1. {t("PersonalInformation")}
                </Accordion.Header>
                <Accordion.Body className="hotel-form-accordion-body">
                  <Row>
                    {/* Full Name (as per passport) */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container" id={`field-full_name_as_per_passport-${activeTab}`}>
                        <label className="hotel-form-input-label">
                          {t("FullNameAsPerPassport")} <span>*</span>
                        </label>
                        <input
                          type="text"
                          className="hotel-form-input"
                          placeholder={t("FullNameAsPerPassport")}
                          value={formsData[item.package_component_id]?.full_name_as_per_passport || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              full_name_as_per_passport: e.target.value,
                            })
                          }
                        />
                        {getError("full_name_as_per_passport") && (
                          <div className="field-error">{getError("full_name_as_per_passport")}</div>
                        )}
                      </div>
                    </Col>

                    {/* Nationality */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container" id={`field-nationality-${activeTab}`}>
                        <label className="hotel-form-input-label">{t("Nationality")}</label>
                        <Autocomplete
                          options={natOptions}
                          loading={natLoading}
                          getOptionLabel={(o) => o.name || ""}
                          value={
                            natOptions.find(
                              (opt) => opt?.name === formsData?.[item.package_component_id]?.nationality
                            ) || null
                          }
                          onInputChange={(_, v) => setNatQuery(v)}
                          onChange={(_, v) =>
                            handleFormChange(item.package_component_id, {
                              nationality: v,
                            })
                          }
                          sx={{
                            ".MuiAutocomplete-inputRoot": {
                              fontSize: "12px",
                              flexDirection: Lang === "ar" ? "row-reverse" : "row",
                            },
                            ".MuiAutocomplete-endAdornment": {
                              left: Lang === "ar" ? "9 !important" : "unset !important",
                              right: Lang === "ar" ? "unset !important" : "9 !important",
                            },
                            ".MuiInputBase-input": {
                              textAlign: Lang === "ar" ? "right" : "left",
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder={t("SelectNationality")}
                              InputProps={{
                                ...params.InputProps,
                                style: {
                                  direction: Lang === "ar" ? "rtl" : "ltr",
                                },
                                sx: {
                                  backgroundColor: "#FFF",
                                  borderRadius: "6px",
                                  padding: "9px 15px !important",
                                  fontFamily: '"Dmsans400"',
                                  fontSize: "14px !important",
                                  fontWeight: 400,
                                  letterSpacing: "0.25px",
                                },
                              }}
                            />
                          )}
                        />
                      </div>
                    </Col>

                    {/* Contact Number (Phone & Email) */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container" id={`field-contact_number-${activeTab}`}>
                        <label className="hotel-form-input-label">{t("contactNumberPhoneAndEmail")}</label>
                        <input
                          type="text"
                          className="hotel-form-input"
                          placeholder={t("contactNumberPhoneAndEmail")}
                          value={formsData[item.package_component_id]?.contact_number || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              contact_number: e.target.value,
                            })
                          }
                        />
                        {getError("contact_number") && (
                          <div className="field-error">{getError("contact_number")}</div>
                        )}
                      </div>
                    </Col>

                    {/* Date of Birth */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container" id={`field-date_of_birth-${activeTab}`}>
                        <label className="hotel-form-input-label">{t("DateOfBirth")}</label>
                        <SingleCalendar
                          value={
                            formsData?.[item.package_component_id]?.date_of_birth
                              ? new Date(formsData[item.package_component_id].date_of_birth)
                              : null
                          }
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              date_of_birth: e.value,
                            })
                          }
                          placeholder={t("DateOfBirth")}
                          showTime={false}
                          max={new Date()}
                        />
                      </div>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              {/* 2. Transfer Details ------------------------------------------------------ */}
              <Accordion.Item eventKey="1" className="hotel-form-accordion-item">
                <Accordion.Header className="hotel-form-accordion-header">
                  2. {t("TransferDetails")}
                </Accordion.Header>
                <Accordion.Body className="hotel-form-accordion-body">
                  <Row>
                    {/* Pickup Location (Airport, Hotel, City Address) */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container" id={`field-pickup_location-${activeTab}`}>
                        <label className="hotel-form-input-label">
                          {t("pickupLocation")} <span>*</span>
                        </label>
                        <input
                          type="text"
                          className="hotel-form-input"
                          placeholder={t("pickupLocation")}
                          value={formsData[item.package_component_id]?.pickup_location || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              pickup_location: e.target.value,
                            })
                          }
                        />
                        {getError("pickup_location") && (
                          <div className="field-error">{getError("pickup_location")}</div>
                        )}
                      </div>
                    </Col>

                    {/* Drop-off Location (Hotel, Airport, Destination Address) */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container" id={`field-dropoff_location-${activeTab}`}>
                        <label className="hotel-form-input-label">
                          {t("dropOffLocation")} <span>*</span>
                        </label>
                        <input
                          type="text"
                          className="hotel-form-input"
                          placeholder={t("dropOffLocation")}
                          value={formsData[item.package_component_id]?.dropoff_location || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              dropoff_location: e.target.value,
                            })
                          }
                        />
                        {getError("dropoff_location") && (
                          <div className="field-error">{getError("dropoff_location")}</div>
                        )}
                      </div>
                    </Col>

                    {/* Pickup Date & Time */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container" id={`field-pickup_datetime-${activeTab}`}>
                        <label className="hotel-form-input-label">
                          {t("pickupDate")} <span>*</span>
                        </label>
                        <SingleCalendar
                          value={
                            formsData?.[item.package_component_id]?.pickup_datetime
                              ? new Date(formsData[item.package_component_id].pickup_datetime)
                              : null
                          }
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              pickup_datetime: e.value,
                            })
                          }
                          placeholder={t("pickupDate")}
                          min={new Date()}
                        />
                        {getError("pickup_datetime") && (
                          <div className="field-error">{getError("pickup_datetime")}</div>
                        )}
                      </div>
                    </Col>

                    {/* Transfer Type */}
                    <Col xl={12}>
                      <div className="add-service-form-checkbox-container">
                        <div className="hotel-form-input-label">{t("transferType")}</div>
                        <Form className="add-service-form-checkboxes">
                          {[
                            { val: "One-Way", label: t("OneWay") },
                            { val: "Round Trip", label: t("RoundTrip") },
                          ].map((opt) => (
                            <Form.Check
                              key={opt.val}
                              inline
                              type="radio"
                              label={opt.label}
                              name="transferType"
                              value={formsData[item.package_component_id]?.transfer_type || ""}
                              checked={formsData[item.package_component_id]?.transfer_type === opt.val}
                              onChange={() =>
                                handleFormChange(item.package_component_id, {
                                  transfer_type: opt.val,
                                })
                              }
                              className="add-service-form-checkbox"
                            />
                          ))}
                        </Form>
                      </div>
                    </Col>

                    {/* Vehicle Type */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container">
                        <label className="hotel-form-input-label">{t("preferredVehicleType")}</label>
                        {renderAutocomplete({
                          options: vehicleOptions,
                          value: formsData[item.package_component_id]?.preferred_vehicle_type || null,
                          setValue: (val) =>
                            handleFormChange(item.package_component_id, {
                              preferred_vehicle_type: val,
                            }),
                          _key: "preferred_vehicle_type",
                        })}
                      </div>
                    </Col>

                    {/* Number of Passengers */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container">
                        <label className="hotel-form-input-label">{t("NumberOfPassengers")}</label>
                        <input
                          type="number"
                          className="hotel-form-input"
                          placeholder={t("NumberOfPassengers")}
                          value={formsData[item.package_component_id]?.number_of_passengers || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              number_of_passengers: e.target.value,
                            })
                          }
                          min="1"
                        />
                      </div>
                    </Col>

                    {/* Special Requests */}
                    <Col xl={12}>
                      <div className="hotel-form-input-container">
                        <label className="hotel-form-input-label">{t("SpecialRequestHint")}</label>
                        <textarea
                          rows="3"
                          className="hotel-form-input"
                          placeholder={t("SpecialRequestHint")}
                          value={formsData[item.package_component_id]?.special_requests || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              special_requests: e.target.value,
                            })
                          }
                        />
                      </div>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              {/* 3. Payment & Billing Information --------------------------------------- */}
              <Accordion.Item eventKey="2" className="hotel-form-accordion-item">
                <Accordion.Header className="hotel-form-accordion-header">
                  3. {t("paymentBillingInformation")}
                </Accordion.Header>
                <Accordion.Body className="hotel-form-accordion-body">
                  <Row>
                    {/* Payment Method */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container">
                        <label className="hotel-form-input-label">{t("PaymentMethod")}</label>
                        {renderAutocomplete({
                          options: paymentOptions,
                          value: formsData[item.package_component_id]?.payment_method || null,
                          setValue: (val) =>
                            handleFormChange(item.package_component_id, {
                              payment_method: val,
                            }),
                          _key: "payment_method",
                        })}
                      </div>
                    </Col>

                    {/* Billing Address */}
                    <Col xl={8} md={6}>
                      <div className="hotel-form-input-container">
                        <label className="hotel-form-input-label">{t("BillingAddress")}</label>
                        <input
                          type="text"
                          className="hotel-form-input"
                          placeholder={t("BillingAddress")}
                          value={formsData[item.package_component_id]?.billing_address || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              billing_address: e.target.value,
                            })
                          }
                        />
                      </div>
                    </Col>

                    {/* Invoice Required */}
                    <Col xl={12}>
                      <div className="add-service-form-checkbox-container">
                        <div className="add-service-form-checkbox-title">{t("InvoiceRequired")}</div>
                        <Form className="add-service-form-checkboxes">
                          {[1, 0].map((val) => (
                            <Form.Check
                              key={val}
                              inline
                              type="radio"
                              label={val === 1 ? t("Yes") : t("No")}
                              name={`invoiceRequired-${item.package_component_id}`}
                              className="add-service-form-checkbox"
                              value={val}
                              checked={formsData[item.package_component_id]?.invoice_required === val}
                              onChange={() =>
                                handleFormChange(item.package_component_id, {
                                  invoice_required: val,
                                })
                              }
                            />
                          ))}
                        </Form>
                      </div>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              {/* 4. Travel & Contact Details ------------------------------------------- */}
              <Accordion.Item eventKey="3" className="hotel-form-accordion-item">
                <Accordion.Header className="hotel-form-accordion-header">
                  4. {t("travelContactDetails")}
                </Accordion.Header>
                <Accordion.Body className="hotel-form-accordion-body">
                  <Row>
                    {/* Flight Details (if applicable) */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container">
                        <label className="hotel-form-input-label">{t("FlightDetailsIfApplicable")}</label>
                        <input
                          type="text"
                          className="hotel-form-input"
                          placeholder={t("FlightDetailsIfApplicable")}
                          value={formsData[item.package_component_id]?.flight_details || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              flight_details: e.target.value,
                            })
                          }
                        />
                      </div>
                    </Col>

                    {/* Hotel Details (if applicable) */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container">
                        <label className="hotel-form-input-label">{t("hotelDetails")}</label>
                        <input
                          type="text"
                          className="hotel-form-input"
                          placeholder={t("hotelDetails")}
                          value={formsData[item.package_component_id]?.hotel_details || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              hotel_details: e.target.value,
                            })
                          }
                        />
                      </div>
                    </Col>

                    {/* Contact Person at Destination (if applicable) */}
                    <Col xl={4} md={6}>
                      <div className="hotel-form-input-container">
                        <label className="hotel-form-input-label">{t("ContactPersonAtDestinationIfApplicable")}</label>
                        <input
                          type="text"
                          className="hotel-form-input"
                          placeholder={t("ContactPersonAtDestinationIfApplicable")}
                          value={formsData[item.package_component_id]?.contact_at_destination || ""}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              contact_at_destination: e.target.value,
                            })
                          }
                        />
                      </div>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              {/* 5. Supporting Documents ---------------------------------------------- */}
              <Accordion.Item eventKey="4" className="hotel-form-accordion-item">
                <Accordion.Header className="hotel-form-accordion-header">
                  5. {t("Supporting Documents")}
                </Accordion.Header>
                <Accordion.Body className="hotel-form-accordion-body">
                  <Row>
                    {[
                      {
                        label: "PassportCopy",
                        label2: "IfRequired",
                        key: "passport_copy",
                      },
                      {
                        label: "FlightTicketIfBooked",
                        label2: "",
                        key: "flight_ticket",
                      },
                    ].map(({ label, key, label2 }) => {
                      const inputKey = `${item.package_component_id}-${key}`;
                      const files = formsData[item.package_component_id]?.files?.[key] || [];
                      const file = Array.isArray(files) ? files[0] : files;

                      const getFilename = (file) => {
                        if (!file) return null;
                        if (file.name) return file.name;
                        if (typeof file.url === "string") {
                          return file.url.split("/").pop();
                        }
                        if (typeof file === "string") {
                          return file.split("/").pop();
                        }
                        return null;
                      };

                      const getFileUrl = (file) => {
                        if (!file) return null;
                        if (file instanceof File || file instanceof Blob) {
                          return URL.createObjectURL(file);
                        }
                        if (typeof file === "string") {
                          return `${url}${file}`;
                        }
                        if (file.url && typeof file.url === "string") {
                          return `${url}${file.url}`;
                        }
                        return null;
                      };

                      const filename = getFilename(file);
                      const fileUrl = getFileUrl(file);
                      const isPdf = (filename && filename.toLowerCase().endsWith(".pdf")) || false;

                      return (
                        <Col xl={6} md={6} key={inputKey}>
                          <div className="hotel-form-input-container">
                            <label className="hotel-form-input-label">
                              {t(label)} {label2 ? `(${t(label2)})` : ""}
                            </label>
                            <div className="custom-upload-wrapper">
                              <input
                                id={`file-${inputKey}`}
                                type="file"
                                accept=".pdf, .png, .jpeg, .jpg"
                                style={{ display: "none" }}
                                ref={(el) => {
                                  if (el) inputRefs.current[inputKey] = el;
                                }}
                                onChange={(e) =>
                                  handleFileChange(item.package_component_id, key, e.target.files[0])
                                }
                              />
                              {file ? (
                                <div className="custom-upload-box">
                                  <div className="file-info">
                                    {isPdf ? (
                                      <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="file-link"
                                      >
                                        {truncateFilename(filename)}
                                        <span className="file-type-badge">PDF</span>
                                      </a>
                                    ) : (
                                      <span className="file-name">{truncateFilename(filename)}</span>
                                    )}
                                  </div>

                                  <div className="file-actions">
                                    <button
                                      type="button"
                                      className="file-action-btn view-file-btn"
                                      onClick={() => {
                                        if (!fileUrl) return;
                                        if (isPdf) {
                                          window.open(fileUrl, "_blank");
                                        } else {
                                          setPreviewImage(fileUrl);
                                          setShowUploadModal(true);
                                        }
                                      }}
                                    >
                                      {isPdf ? t("View") : t("Preview")}
                                    </button>

                                    <button
                                      type="button"
                                      className="file-action-btn remove-file-btn"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleFileRemove(item.package_component_id, key);
                                      }}
                                    >
                                      {t("Remove")}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <label htmlFor={`file-${inputKey}`} className="custom-upload-box">
                                  <span className="upload-placeholder">{t("supportedImageFormats")}</span>
                                  <UploadIcon className="upload-svg-icon" />
                                </label>
                              )}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              {/* 6. Declaration & Signature ------------------------------------------- */}
              <Accordion.Item eventKey="5" className="hotel-form-accordion-item">
                <Accordion.Header className="hotel-form-accordion-header">
                  6. {t("DeclarationAndSignature")}
                </Accordion.Header>
                <Accordion.Body className="hotel-form-accordion-body">
                  <Row>
                    <Col xl={12}>
                      <div className="hotel-form-input-container" id={`field-declaration_agreed-${activeTab}`}>
                        <Form.Check
                          type="checkbox"
                          label={t("IAgreeToTheTermsAndConditions")}
                          className="hotel-form-input-label"
                          checked={formsData[item.package_component_id]?.declaration_agreed || false}
                          onChange={(e) =>
                            handleFormChange(item.package_component_id, {
                              declaration_agreed: e.target.checked,
                            })
                          }
                        />
                        {getError("declaration_agreed") && (
                          <div className="field-error">{getError("declaration_agreed")}</div>
                        )}
                      </div>
                    </Col>

                    <Col xl={6}>
                      <div className="hotel-form-input-container" id={`field-digital_or_physical_signature-${activeTab}`}>
                        <label className="hotel-form-input-label">
                          {t("Signature")} <span>*</span>
                        </label>
                        <div className="signature-container" style={{ position: "relative" }}>
                          <SignatureCanvas
                            ref={sigPadRef}
                            penColor="black"
                            canvasProps={{
                              width: 500,
                              height: 150,
                              className: "sigCanvas",
                            }}
                            onEnd={() =>
                              handleSignatureChange(item.package_component_id, sigPadRef.current.toDataURL())
                            }
                          />
                          <button
                            type="button"
                            title="Clear"
                            onClick={() => {
                              sigPadRef.current.clear();
                              handleSignatureChange(item.package_component_id, "");
                            }}
                            style={{
                              position: "absolute",
                              top: 8,
                              right: 40,
                              background: "none",
                              border: "none",
                              fontSize: 18,
                            }}
                          >
                            ⟳
                          </button>
                        </div>
                        {getError("digital_or_physical_signature") && (
                          <div className="field-error">{getError("digital_or_physical_signature")}</div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {previewImage && (
                <div
                  className={`modal fade ${showUploadModal ? "show d-block" : ""} service-files-modal`}
                  tabIndex="-1"
                  role="dialog"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                  onClick={() => setShowUploadModal(false)}
                >
                  <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                      <button type="button" className="close" onClick={() => setShowUploadModal(false)}></button>
                      <div className="service-image-file">
                        <img src={previewImage} alt="preview" style={{ maxWidth: "100%" }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Accordion>
          ))}

        {/* footer */}
        <Row>
          <Col>
            <div className="add-services-footer-buttons">
              <button className="btn-next" onClick={handleNext} disabled={saving}>
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {isEditMode ? t("Updating…") : t("Saving…")}
                  </>
                ) : activeTab === lastTabKey ? (
                  isEditMode ? (
                    t("Update")
                  ) : (
                    t("Finish")
                  )
                ) : (
                  t("Next & Save")
                )}
              </button>
            </div>
          </Col>
        </Row>

        {/* upload modal */}
        <Modal centered show={showModal} backdrop="static" keyboard={false}>
          <Modal.Header>
            <Modal.Title>{t("uploadingFiles")}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: 300, overflowY: "auto" }}>
            {uploads.map((u) => (
              <div key={u.key} style={{ marginBottom: 15 }}>
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-truncate" style={{ maxWidth: "85%" }}>
                    {u.name}
                  </small>
                  <small>{u.progress}%</small>
                </div>
                <ProgressBar now={u.progress} striped animated />
              </div>
            ))}
          </Modal.Body>
        </Modal>
        <ErrorPopUp
          message={globalErr}
          showpopUp={errorPopup}
          handleClose={() => {
            setErrorPopUp(false);
            setGlobalErr(null);
          }}
        />
        <ToastContainer position="bottom-right" hideProgressBar="true" type="error" />
      </div>
    );
  }
);

export default TransfersForm;